import React, { type ReactNode, useEffect } from 'react';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ObjectActivityTimelineContainer } from '@atlassian/jira-servicedesk-insight-object-activity-timeline-store/src/controllers/store/index.tsx';
import { ObjectDetailsContainer } from '@atlassian/jira-servicedesk-insight-object-details-store/src/ui/index.tsx';
import type { InsightObjectDetailsResponse } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import {
	type CmdbObjectId,
	toCmdbObjectId,
	type WorkspaceId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { GenericError } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/errors/index.tsx';
import { InsightObjectViewSkeleton } from '@atlassian/jira-skeletons/src/ui/insight-object/InsightObjectViewSkeleton.tsx';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useFetchObjectDetails } from '../services/fetch-object-details/index.tsx';

export type Props = {
	workspaceId: WorkspaceId;
	objectId: CmdbObjectId;
	initialObjectDetailsData?: InsightObjectDetailsResponse;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	children: ReactNode;
	onUpdate: () => void;
};

export const ObjectViewContainer = (props: Props) => {
	const {
		workspaceId,
		objectId,
		initialObjectDetailsData,
		createAnalyticsEvent,
		children,
		onUpdate,
	} = props;

	const {
		data: fetchData,
		error: fetchError,
		loading: fetchLoading,
		fetchObjectDetails: fetchObjectDetailsRequest,
	} = useFetchObjectDetails(workspaceId, createAnalyticsEvent);

	const objectDetails = fetchData ?? initialObjectDetailsData;

	useEffect(() => {
		// auto fetch the initial object details if initialObjectDetailsData is not provided or on object change
		if (!fetchLoading) {
			if (!objectDetails || objectDetails.id !== objectId) {
				fetchObjectDetailsRequest(objectId);
			}
		}
	}, [fetchLoading, fetchObjectDetailsRequest, objectDetails, objectId]);

	useEffect(() => {
		if (fetchError) {
			GlobalPageLoadExperience.failure({ force: true });
		}
	}, [fetchError]);

	if (fetchLoading) {
		return <InsightObjectViewSkeleton />;
	}
	if (fetchError) {
		return <GenericError />;
	}

	return objectDetails != null ? (
		<ObjectDetailsContainer
			scope={objectId}
			objectId={toCmdbObjectId(objectId)}
			workspaceId={workspaceId}
			initialObjectDetailsData={objectDetails}
			createAnalyticsEvent={createAnalyticsEvent}
			onUpdate={onUpdate}
		>
			<ObjectActivityTimelineContainer
				workspaceId={workspaceId}
				createAnalyticsEvent={createAnalyticsEvent}
				objectId={toCmdbObjectId(objectId)}
			>
				{children}
			</ObjectActivityTimelineContainer>
		</ObjectDetailsContainer>
	) : (
		<InsightObjectViewSkeleton />
	);
};
